import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Path as BreadcrumbPath } from 'src/components/molecules/BreadcrumbsPipe'
import useAppTitle from 'src/hooks/useAppTitle'
import {
  useFilteredCalculationItems,
  useFilteredCalculationDSLs,
} from 'src/hooks/useFilteredCalculationData'
import Path, { TenantPathParams } from 'src/routes/path'
import {
  getCalculationDSLs,
  selectCalculationDSLsRequestStateByParams,
} from 'src/slices/calculationDSLs/calculationDSLsSlice'
import {
  getCalculationGroup,
  selectCalculationGroupStateByParams,
} from 'src/slices/calculationGroups/calculationGroupsSlice'
import {
  putEditReadItems,
  EditReadItem as EditReadItemPayload,
  clearEditCalculation,
} from 'src/slices/editCalculations/editCalculationsSlice'
import {
  getStore,
  selectStoresRequestStateByParams,
  selectStoreByCode,
} from 'src/slices/stores/storesSlice'
import {
  getTenant,
  selectTenantStateByCode,
  selectTenantByCode,
} from 'src/slices/tenants/tenantsSlice'
import { useAppDispatch, useAppSelector } from 'src/store'
import EditReadItemTemplate from '../templates'
import Presenter from './presenter'

const EditReadItem: React.FC = (): ReactElement => {
  useAppTitle('利用設定 ステップ1')
  const { orgCode, storeCode, tenantCode } =
    useParams<TenantPathParams>() as TenantPathParams
  const storeParams = useMemo(() => {
    return {
      orgCode,
      storeCode,
    }
  }, [orgCode, storeCode])
  const tenantParams = useMemo(() => {
    return {
      orgCode,
      storeCode,
      tenantCode,
    }
  }, [orgCode, storeCode, tenantCode])
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const store = useAppSelector(selectStoreByCode(storeParams))
  const tenant = useAppSelector(selectTenantByCode(tenantParams))
  // 三井不動産（orgCode: 007）では非表示項目をフィルタリング
  const calculationItems = useFilteredCalculationItems(storeParams)
  const editReadItems = useAppSelector(
    (state) => state.forms.editCalculations.editReadItems
  )
  // 三井不動産（orgCode: 007）では非表示項目をフィルタリング
  const dsls = useFilteredCalculationDSLs(tenantParams)
  const tenantRequestState = useAppSelector(
    selectTenantStateByCode(tenantParams)
  )
  const storeRequestState = useAppSelector(
    selectStoresRequestStateByParams(storeParams)
  )
  const calculationGroupRequestState = useAppSelector(
    selectCalculationGroupStateByParams(storeParams)
  )

  const dslRequestState = useAppSelector(
    selectCalculationDSLsRequestStateByParams(tenantParams)
  )

  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbPath[]>([])
  useEffect(() => {
    setBreadcrumbs(Presenter.breadcrumbs(store?.name, tenant?.name))
  }, [store?.name, tenant?.name])

  // API Request
  useEffect(() => {
    if (tenantRequestState.status === 'idle') {
      dispatch(getTenant(tenantParams))
    }
  }, [dispatch, tenantParams, tenantRequestState.status])
  useEffect(() => {
    if (store === undefined && storeRequestState.status === 'idle') {
      dispatch(getStore(storeParams))
    }
  }, [dispatch, store, storeParams, storeRequestState.status])
  useEffect(() => {
    if (calculationGroupRequestState.status === 'idle') {
      dispatch(getCalculationGroup(storeParams))
    }
  }, [dispatch, storeParams, calculationGroupRequestState.status])
  useEffect(() => {
    if (dslRequestState.status === 'idle' && editReadItems.length === 0) {
      dispatch(getCalculationDSLs(tenantParams))
    }
  }, [dispatch, dslRequestState, editReadItems.length, tenantParams])

  // restore state data
  const handleClickGoForward = (updatedEditReadItem: EditReadItemPayload[]) => {
    dispatch(putEditReadItems(updatedEditReadItem))
    navigate(Path.editOCRFormat(orgCode, storeCode, tenantCode))
  }

  const handleClickConfirmDiscard = () => {
    dispatch(clearEditCalculation())
    navigate(Path.tenantCalculationTab(orgCode, storeCode, tenantCode))
  }
  const handleUpdateEditReadItem = useCallback(
    (restoredEditReadItems: EditReadItemPayload[]) => {
      dispatch(putEditReadItems(restoredEditReadItems))
    },
    [dispatch]
  )

  return (
    <EditReadItemTemplate
      calculationItems={calculationItems}
      editReadItems={editReadItems}
      dsls={dsls}
      breadcrumbs={breadcrumbs}
      onClickGoForward={handleClickGoForward}
      onClickConfirmDiscard={handleClickConfirmDiscard}
      onUpdateEditReadItem={handleUpdateEditReadItem}
    />
  )
}

export default EditReadItem
